/**
 * RecalcPolicyModal
 * ------------------
 * Opens when the user clicks "Recalculate" and there are STARVED tasks
 * (blank actuals + scheduled_start already in the past).  Shows the
 * side-by-side impact preview for all 4 forecast-date policies:
 *   a) Today
 *   b) Next working day
 *   c) Driving predecessor
 *   d) Flag STARVED only
 * User picks one and clicks Apply — parent then invokes the recalculate
 * endpoint with ?policy=<a|b|c|d>.
 */
import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, CalendarCheck, GitBranch, Flag, AlertTriangle, ChevronRight, Loader2 } from 'lucide-react';

const POLICIES = [
  {
    key: 'a',
    title: 'Today',
    subtitle: 'Slide forecast start to today',
    Icon: Calendar,
    accent: '#3b82f6',
    hint: 'Best when work restarts immediately regardless of predecessor status',
  },
  {
    key: 'b',
    title: 'Next working day',
    subtitle: 'Slide to the next working day',
    Icon: CalendarCheck,
    accent: '#22c55e',
    hint: 'Same as Today, but respects project calendar (skips weekends/holidays)',
  },
  {
    key: 'c',
    title: 'Behind driving predecessor',
    subtitle: 'Forecast off the driving predecessor finish',
    Icon: GitBranch,
    accent: '#f59e0b',
    hint: 'Uses the latest predecessor finish + 1 working day (network-consistent)',
  },
  {
    key: 'd',
    title: 'Flag STARVED only',
    subtitle: 'No date changes — just tag the task',
    Icon: Flag,
    accent: '#ef4444',
    hint: 'PM prefers to chase progress updates rather than move dates',
  },
];

const fmtDate = (d) => (d ? String(d).slice(0, 10) : '—');
const fmtDelta = (n) => {
  if (n === null || n === undefined) return '—';
  if (n === 0) return '0d';
  return n > 0 ? `+${n}d` : `${n}d`;
};

export default function RecalcPolicyModal({
  open,
  onClose,
  loading,
  preview,          // { starved_count, starved_tasks[], baseline_project_finish, policies[] }
  applying,
  onApply,          // (policyKey) => void
  onSkip,           // () => void — run CPM without any policy
  baselineFinish,   // project's finish BEFORE preview
}) {
  const [selected, setSelected] = useState('a');

  const starvedCount = preview?.starved_count || 0;
  const noStarved = !loading && preview && starvedCount === 0;
  const policyMap = Object.fromEntries((preview?.policies || []).map((p) => [p.policy, p]));
  const baseline = baselineFinish || preview?.baseline_project_finish;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !applying && onClose?.()}>
      <DialogContent
        className="max-w-6xl"
        style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}
        data-testid="recalc-policy-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
            <AlertTriangle className="w-5 h-5 text-amber-400" />
            Recalculate — pick a forecast policy
          </DialogTitle>
          <DialogDescription style={{ color: 'var(--text-muted)' }}>
            {loading ? (
              <span className="flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Running dry-run preview across all 4 policies…</span>
            ) : noStarved ? (
              <span className="flex items-center gap-2 text-emerald-400">
                <span className="inline-block w-2 h-2 rounded-full bg-emerald-400" />
                No starved tasks detected — every leaf task has either an actual start or a scheduled start that's still in the future. You can safely skip policy selection and run a plain CPM recalculate.
              </span>
            ) : (
              <>
                <span className="font-semibold text-amber-400">{starvedCount}</span> task{starvedCount === 1 ? '' : 's'} have blank actuals with a scheduled start already in the past.
                {' '}Preview shows the impact of each policy — CPM will re-flow downstream successors after you apply.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {/* Policy cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="recalc-policy-grid">
          {POLICIES.map((p) => {
            const m = policyMap[p.key] || {};
            const isSelected = selected === p.key;
            const finishDelta = m.new_project_finish && baseline
              ? Math.round((new Date(m.new_project_finish) - new Date(baseline)) / 86400000)
              : null;
            const Icon = p.Icon;
            return (
              <button
                key={p.key}
                type="button"
                onClick={() => setSelected(p.key)}
                disabled={applying}
                className="text-left rounded-lg border p-3 transition-all hover:scale-[1.01]"
                style={{
                  background: isSelected ? `${p.accent}12` : 'var(--card-bg-alt)',
                  borderColor: isSelected ? p.accent : 'var(--border-color)',
                  borderWidth: isSelected ? 2 : 1,
                  cursor: applying ? 'not-allowed' : 'pointer',
                  opacity: applying && !isSelected ? 0.5 : 1,
                }}
                data-testid={`recalc-policy-card-${p.key}`}
              >
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-md flex items-center justify-center"
                         style={{ background: `${p.accent}20`, color: p.accent }}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="text-xs font-bold uppercase tracking-wide" style={{ color: p.accent }}>
                        Policy {p.key.toUpperCase()}
                      </div>
                      <div className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                        {p.title}
                      </div>
                    </div>
                  </div>
                  {isSelected && (
                    <Badge className="text-[10px]" style={{ background: p.accent, color: '#fff' }}>
                      Selected
                    </Badge>
                  )}
                </div>

                <div className="text-[11px] mb-3" style={{ color: 'var(--text-muted)' }}>
                  {p.subtitle}
                </div>

                {/* Metrics */}
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between text-[11px]">
                    <span style={{ color: 'var(--text-muted)' }}>Tasks moved</span>
                    <span className="font-mono font-semibold" style={{ color: 'var(--text-primary)' }} data-testid={`recalc-policy-${p.key}-moved`}>
                      {loading ? '…' : m.tasks_moved ?? 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-[11px]">
                    <span style={{ color: 'var(--text-muted)' }}>Avg days moved</span>
                    <span className="font-mono font-semibold" style={{ color: 'var(--text-primary)' }}>
                      {loading ? '…' : fmtDelta(m.avg_days_moved ?? 0)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-[11px]">
                    <span style={{ color: 'var(--text-muted)' }}>Max days moved</span>
                    <span className="font-mono font-semibold" style={{ color: 'var(--text-primary)' }}>
                      {loading ? '…' : `${m.max_days_moved ?? 0}d`}
                    </span>
                  </div>
                  <div className="pt-1.5 mt-1.5 border-t" style={{ borderColor: 'var(--border-color)' }}>
                    <div className="flex items-center justify-between text-[11px]">
                      <span style={{ color: 'var(--text-muted)' }}>New project finish</span>
                      <span className="font-mono font-semibold" style={{ color: 'var(--text-primary)' }} data-testid={`recalc-policy-${p.key}-finish`}>
                        {loading ? '…' : fmtDate(m.new_project_finish)}
                      </span>
                    </div>
                    {finishDelta !== null && finishDelta !== 0 && (
                      <div className="flex items-center justify-end gap-1 mt-0.5">
                        <span className="text-[10px] font-mono"
                              style={{ color: finishDelta > 0 ? '#ef4444' : '#22c55e' }}>
                          {finishDelta > 0 ? '▲' : '▼'} {fmtDelta(finishDelta)} vs baseline
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-3 pt-2 border-t text-[10px] leading-relaxed"
                     style={{ borderColor: 'var(--border-color)', color: 'var(--text-muted)' }}>
                  {p.hint}
                </div>
              </button>
            );
          })}
        </div>

        {/* Starved task preview list */}
        {!loading && preview?.starved_tasks?.length > 0 && (
          <div className="mt-2 rounded-lg border overflow-hidden"
               style={{ background: 'var(--card-bg-alt)', borderColor: 'var(--border-color)' }}>
            <div className="px-3 py-2 border-b text-[11px] font-semibold"
                 style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}>
              Starved tasks ({preview.starved_tasks.length}
              {preview.starved_count > preview.starved_tasks.length ? ` of ${preview.starved_count}` : ''})
            </div>
            <div className="max-h-48 overflow-y-auto">
              <table className="w-full text-[10px]" data-testid="recalc-starved-table">
                <thead className="sticky top-0" style={{ background: 'var(--card-bg-alt)' }}>
                  <tr>
                    <th className="p-1.5 text-left font-medium" style={{ color: 'var(--text-muted)' }}>WBS</th>
                    <th className="p-1.5 text-left font-medium" style={{ color: 'var(--text-muted)' }}>Task</th>
                    <th className="p-1.5 text-right font-medium" style={{ color: 'var(--text-muted)' }}>Scheduled Start</th>
                    <th className="p-1.5 text-right font-medium" style={{ color: 'var(--text-muted)' }}>Scheduled Finish</th>
                    <th className="p-1.5 text-right font-medium" style={{ color: 'var(--text-muted)' }}>% Done</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.starved_tasks.map((t) => (
                    <tr key={t.id} className="border-t" style={{ borderColor: 'var(--border-color)' }}>
                      <td className="p-1.5 font-mono" style={{ color: 'var(--accent-primary)' }}>{t.wbs_code || '—'}</td>
                      <td className="p-1.5 truncate max-w-[280px]" style={{ color: 'var(--text-primary)' }}>
                        <span className="inline-flex items-center gap-1">
                          {t.is_critical && <AlertTriangle className="w-2.5 h-2.5 text-red-400" />}
                          {t.name}
                        </span>
                      </td>
                      <td className="p-1.5 text-right font-mono text-amber-400">{fmtDate(t.scheduled_start)}</td>
                      <td className="p-1.5 text-right font-mono" style={{ color: 'var(--text-muted)' }}>{fmtDate(t.scheduled_finish)}</td>
                      <td className="p-1.5 text-right font-mono" style={{ color: 'var(--text-muted)' }}>{Math.round(t.percent_complete)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={applying}
            data-testid="recalc-policy-cancel"
          >
            Cancel
          </Button>
          {onSkip && (
            <Button
              variant="outline"
              onClick={onSkip}
              disabled={applying || loading}
              className="border-slate-500/40 text-slate-200 hover:bg-slate-500/10"
              data-testid="recalc-policy-skip"
            >
              Skip Policy · Just Recalculate
            </Button>
          )}
          <Button
            onClick={() => onApply?.(selected)}
            disabled={applying || loading || noStarved}
            className="bg-emerald-600 hover:bg-emerald-500 text-white disabled:opacity-40"
            data-testid="recalc-policy-apply"
          >
            {applying ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Applying policy {selected.toUpperCase()}…</>
            ) : (
              <>Apply policy {selected.toUpperCase()} <ChevronRight className="w-4 h-4 ml-1" /></>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
